'use client';

import { motion } from 'framer-motion';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { mergeRefs } from '@zoomit/primitives';
import { cssVar, theme } from '@zoomit/zoomit-ds';

import { useEventCallbacks } from '../hooks/useEventCallbacks';
import type { SheetContainerProps } from '../types';
import { useSheetContext } from './context';

const SheetContainer = forwardRef<any, SheetContainerProps>(
  (
    { children, style = {}, preventOverlayClick = true, hasBorderRadius = true, isFullScreen, ...rest },
    ref,
  ) => {
    const {
      y,
      isOpen,
      callbacks,
      snapPoints,
      initialSnap = 0,
      sheetRef,
      windowHeight,
      springConfig,
      fromTop,
    } = useSheetContext();

    const { handleAnimationComplete } = useEventCallbacks(isOpen, callbacks);
    const initialY = snapPoints ? snapPoints[0] - snapPoints[initialSnap] : 0;

    return (
      <Container
        {...rest}
        $preventOverlayClick={preventOverlayClick}
        $fromTop={fromTop}
        ref={mergeRefs([sheetRef, ref])}
        className="react-modal-sheet-container"
        style={{ height: isFullScreen ? '100%' : 'auto', ...style, y }}
        initial={{ y: fromTop ? -1 * windowHeight : windowHeight }}
        animate={{
          y: initialY,
          transition: { type: 'spring', ...springConfig },
        }}
        exit={{ y: fromTop ? -1 * windowHeight : windowHeight }}
        onAnimationComplete={handleAnimationComplete}
        $hasBorderRadius={hasBorderRadius}
      >
        {children}
      </Container>
    );
  },
);

const Container = styled(motion.div)<{
  $fromTop: boolean;
  $hasBorderRadius?: boolean;
  $preventOverlayClick: boolean;
}>`
  z-index: 2;
  position: absolute;
  flex-grow: 1;
  left: 0;
  ${({ $fromTop }) => ($fromTop ? 'top: 0' : 'bottom: 0')};
  ${({ $hasBorderRadius, $fromTop }) =>
    $hasBorderRadius &&
    ($fromTop
      ? `border-radius: 0 0 var(${theme.radius.md}) var(${theme.radius.md})`
      : `border-radius: var(${theme.radius.md}) var(${theme.radius.md}) 0 0`)};
  width: 100%;
  overflow: hidden;
  background-color: ${cssVar(theme.card)};
  box-shadow: 0px -2px 16px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  ${({ $preventOverlayClick }) => $preventOverlayClick && `pointer-events: auto;`}
`;

export default SheetContainer;
